/** Browser-side bridge to the canonical versioned business-event pipeline. */

import {
  EVENT_TYPES,
  getAnalyticsTracker,
  type BrowserEventTypeLiteral,
  type JsonObject,
} from '@ecommerce/analytics/browser';
import { createPixelEventId } from './analytics-event-id';

export { EVENT_TYPES };

/**
 * Build the durable canonical id that corresponds to a golden tracking event.
 * UI telemetry may carry this value as businessEventId, but keeps its own
 * independent event_id/deduplication lifecycle.
 */
export function buildBrowserBusinessEventId(
  eventType: BrowserEventTypeLiteral,
  sourceEventId: string,
): string {
  return `${eventType}:${sourceEventId}`;
}

/**
 * Publish a canonical business event through the storefront BFF.
 *
 * This is deliberately separate from both `dataLayer` and the funnel UI bus:
 * `ui.*` remains telemetry about interactions, while these versioned
 * names represent commerce/domain facts.
 */
export function publishBrowserBusinessEvent<T extends JsonObject>(
  eventType: BrowserEventTypeLiteral,
  data: T,
  trackingEventId?: string,
): string {
  const sourceEventId = trackingEventId ?? createPixelEventId();
  const businessEventId = buildBrowserBusinessEventId(eventType, sourceEventId);
  getAnalyticsTracker()?.track(eventType, data, {
    eventId: businessEventId,
    ...(trackingEventId ? { correlationId: trackingEventId } : {}),
  });
  return businessEventId;
}
